export interface BubbleOptions {
  url: string;
  group: string;
  keepChatOpen?: boolean;
}

/**
 * Bubble is a placeholder for the main chatbot panel. The placeholder consists
 * of an overlaid avatar image. Activating the avatar will load the main
 * conversation pane application.
 *
 * Optionally, a Call-to-Action text is shown in a speech bubble next to the
 * avatar.
 */
export class Bubble {
  public element: HTMLElement;
  private iframe: HTMLIFrameElement;
  private options: BubbleOptions;
  private initialized = false;
  private intervalId;
  private baseHost: string;
  private keepChatOpen: boolean;

  constructor(options: BubbleOptions) {
    this.element = Template.defaultBubbleTemplate();
    this.element.classList.add('invisible');
    this.options = this.applyDefaultOptions(options);
    this.keepChatOpen = options.keepChatOpen ?? false;

    const pathArray = this.options.url.split('/');

    this.baseHost = `${pathArray[0]}//${pathArray[2]}`;

    window.addEventListener('kauz.chat.open', (_event: CustomEvent) => {
      if (!this.initialized) {
        this.init();
      }
      this.open();
      if (this.keepChatOpen) {
        localStorage.setItem(`c_chat_open_${this.options.group}`, '1');
      }
    });

    window.addEventListener('kauz.chat.close', (_event: CustomEvent) => {
      this.close();
      if (this.keepChatOpen) {
        localStorage.removeItem(`c_chat_open_${this.options.group}`);
      }
    });

    window.addEventListener('resize', (_: UIEvent) => {
      window.dispatchEvent(
        new CustomEvent('kauz.chat.resize', { detail: this }),
      );
    });

    this.element.addEventListener('mouseenter', (_: MouseEvent) => {
      window.dispatchEvent(
        new CustomEvent('kauz.chat.enter', { detail: this }),
      );
    });
    this.element.addEventListener('mouseleave', (_: MouseEvent) => {
      window.dispatchEvent(
        new CustomEvent('kauz.chat.leave', {
          detail: this,
        }),
      );
    });

    window.addEventListener('message', (e) => {      
      if (e.origin !== this.baseHost) {
        return;
      }

      switch (e.data.event || '') {
        case 'kauz.chat.iframeInitEnd':
          window.clearInterval(this.intervalId);
          this.observeLangAttribute();
          break;
        case 'kauz.chat.close':
          window.dispatchEvent(
            new CustomEvent('kauz.chat.close', { detail: { ...e.data } }),
          );
          break;
        case 'kauz.chat.liveChatOpen':
          window.dispatchEvent(
            new CustomEvent('kauz.chat.liveChatOpen', { detail: {} }),
          );
          break;
        case 'kauz.link.open':
          window.dispatchEvent(
            new CustomEvent('kauz.link.open', {
              detail: { url: e.data.url },
            }),
          );
          break;
        case 'kauz.phonelink.open':
          window.dispatchEvent(
            new CustomEvent('kauz.phonelink.open', {
              detail: { url: e.data.url },
            }),
          );
          break;
        case 'kauz.language.request':
          // set logic for getting page language
          try {
            const parentDocumentLanguage = window.document.documentElement.lang;
            console.log('Parent page language:', parentDocumentLanguage);
            this.sendLanguage(parentDocumentLanguage);
          } catch (error) {
            console.error('Unable to get the parent page language:', error);
          }
          break;
        default:
          break;
      }
    });
  }

  private init(): void {
    this.element.appendChild(this.createFrame());
    this.initialized = true;
  }

  autoOpenIfNeeded(): void {
    if (
      this.keepChatOpen &&
      localStorage.getItem(`c_chat_open_${this.options.group}`)
    ) {
      window.dispatchEvent(new CustomEvent('kauz.chat.open', { detail: this }));
    }
  }

  private applyDefaultOptions(options: BubbleOptions): BubbleOptions {
    return Object.assign({}, {}, options);
  }

  private createFrame(): HTMLElement {
    const div = document.createElement('div');
    this.iframe = document.createElement('iframe');
    const iframeSrc = new URL(this.options.url);
    iframeSrc.searchParams.set('parentUrl', window.location.href);
    this.iframe.setAttribute('src', iframeSrc.toString());
    this.iframe.setAttribute('allow', 'autoplay; fullscreen; microphone; clipboard-read; clipboard-write');
    this.iframe.setAttribute('title', 'Chatbot');
    div.appendChild(this.iframe);

    this.intervalId = window.setInterval(() => {
      this.iframe.contentWindow.postMessage(
        { type: 'kauz.chat.inIframe' },
        this.baseHost,
      );
    }, 500);

    return div;
  }

  open(): void {
    this.element.classList.remove('invisible');
  }

  close(): void {
    this.element.classList.add('invisible');
  }

  getElement(): HTMLElement {
    return this.element;
  }

  sendLanguage(lang: string): void {
    this.iframe.contentWindow.postMessage(
      { type: 'kauz.language.change', lang },
      this.baseHost,
    );
  }
  
  observeLangAttribute(): void {
    // set logic for observing page language
    const targetNode = document.documentElement;
    const config = { attributes: true, attributeFilter: ['lang'] };
    
    const callback = (mutationsList: any) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'lang'
        ) {
          const requiredLang = targetNode.getAttribute('lang');
          this.sendLanguage(requiredLang);
        }
      }
    };
    const observer = new MutationObserver(callback);
    observer.observe(targetNode, config);
    console.log('Observation of the lang attribute change for bot started.');
  }
}

/**
 * Template holds HTML elements used by chatbot placeholder UI.
 */
class Template {
  static defaultBubbleTemplate(): HTMLElement {
    const element = document.createElement('div');
    element.id = 'chat-ui';
    element.classList.add('invisible');

    return element;
  }
}
